import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
    Tooltip,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useEffect, useState } from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import { AddCircleOutline, Delete, Edit, Assessment, ListAlt } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { urls } from "@routes";
import { Survey } from "@admin/types/survey.types";

interface PageData {
    status_code: number;
    status_text: string;
    surveys: Survey[];
    total_count: number;
}

export default function AdminSurveys() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [pageData, setPageData] = useState<PageData>();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);

    // Form state
    const [formTitle, setFormTitle] = useState("");
    const [formDescription, setFormDescription] = useState("");
    const [formDisplayOrder, setFormDisplayOrder] = useState("0");
    const [formIsActive, setFormIsActive] = useState(true);
    const [rewardEmailsEnabled, setRewardEmailsEnabled] = useState(false);    
    const [formRewardCredits, setFormRewardCredits] = useState("250");
    const [rewardDomainsEnabled, setRewardDomainsEnabled] = useState(false);
    const [rewardDomainsCount, setRewardDomainsCount] = useState('1');
    const [rewardCustomEnabled, setRewardCustomEnabled] = useState(false);
    const [rewardCustomText, setRewardCustomText] = useState('');

    // Query for fetching surveys
    const pageDataQuery = useQuery({
        queryKey: ["surveysPageData"],
        queryFn: () => authenticateAndFetchData("/admin/surveys/"),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            setPageData(pageDataQuery.data.data);
        }
    }, [pageDataQuery.data]);

    // Mutation for creating survey
    const createSurveyMutation = useMutation({
        mutationKey: ["createSurvey"],
        mutationFn: () => authenticateAndPostData("/admin/surveys/create/", {
            title: formTitle,
            description: formDescription || null,
            display_order: parseInt(formDisplayOrder) || 0,
            is_active: formIsActive,
            reward_emails_enabled: rewardEmailsEnabled,            
            reward_credits: parseInt(formRewardCredits) || 250,
            reward_domains_enabled: rewardDomainsEnabled,
            reward_domains_count: rewardDomainsEnabled ? (parseInt(rewardDomainsCount, 10) || 0) : 0,
            reward_custom_enabled: rewardCustomEnabled,
            reward_custom_text: rewardCustomEnabled ? rewardCustomText : '',
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Survey created successfully", { variant: "success" });
            setCreateDialogOpen(false);
            resetForm();
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message || "Failed to create survey", {
                variant: "error",
            });
        }
    });

    // Mutation for updating survey
    const updateSurveyMutation = useMutation({
        mutationKey: ["updateSurvey"],
        mutationFn: () => authenticateAndPostData("/admin/surveys/update/", {
            survey_uid: selectedSurvey?.uid,
            title: formTitle,
            description: formDescription || null,
            display_order: parseInt(formDisplayOrder) || 0,
            is_active: formIsActive,
            reward_emails_enabled: rewardEmailsEnabled,            
            reward_credits: parseInt(formRewardCredits) || 250,
            reward_domains_enabled: rewardDomainsEnabled,
            reward_domains_count: rewardDomainsEnabled ? (parseInt(rewardDomainsCount, 10) || 0) : 0,
            reward_custom_enabled: rewardCustomEnabled,
            reward_custom_text: rewardCustomEnabled ? rewardCustomText : '',
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Survey updated successfully", { variant: "success" });
            setEditDialogOpen(false);
            resetForm();
            setSelectedSurvey(null);
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message || "Failed to update survey", {
                variant: "error",
            });
        }
    });

    // Mutation for deleting survey
    const deleteSurveyMutation = useMutation({
        mutationKey: ["deleteSurvey"],
        mutationFn: (uid: string) => authenticateAndPostData("/admin/surveys/delete/", {
            survey_uid: uid,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            pageDataQuery.refetch().then();
            enqueueSnackbar("Survey deleted successfully", { variant: "success" });
            setDeleteDialogOpen(false);
            setSelectedSurvey(null);
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message || "Failed to delete survey", {
                variant: "error",
            });
        }
    });

    const resetForm = () => {
        setFormTitle("");
        setFormDescription("");
        setFormDisplayOrder("0");
        setFormIsActive(true);
        setRewardEmailsEnabled(false)
        setFormRewardCredits("250");
        setRewardDomainsEnabled(false)
        setRewardDomainsCount("1")
        setRewardCustomEnabled(false)
        setRewardCustomText("")
    };

    const handleCreateClick = () => {
        resetForm();
        setCreateDialogOpen(true);
    };

    const handleEditClick = (survey: Survey) => {
        setSelectedSurvey(survey);
        setFormTitle(survey.title);
        setFormDescription(survey.description || "");
        setFormDisplayOrder(survey.display_order.toString());
        setFormIsActive(survey.is_active);
        setRewardEmailsEnabled(survey.reward_emails_enabled)
        setFormRewardCredits(survey.reward_credits.toString());        
        setRewardDomainsEnabled(survey.reward_domains_enabled)
        setRewardDomainsCount(survey.reward_domains_count.toString());
        setRewardCustomEnabled(survey.reward_custom_enabled)
        setRewardCustomText(survey.reward_custom_text)
        setEditDialogOpen(true);
    };

    const handleDeleteClick = (survey: Survey) => {
        setSelectedSurvey(survey);
        setDeleteDialogOpen(true);
    };

    const columns: GridColDef<Survey>[] = [
        {
            field: "display_order",
            headerName: "Order",
            width: 70,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "title",
            headerName: "Survey Title",
            flex: 1,
            minWidth: 200,
            align: "left",
            headerAlign: "center",
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <Typography
                        variant="body2"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                        }}
                    >
                        {params.value}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
            minWidth: 150,
            align: "left",
            headerAlign: "center",
            renderCell: (params) => (
                <Tooltip title={params.value || ""}>
                    <Typography
                        variant="body2"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                        }}
                    >
                        {params.value || "—"}
                    </Typography>
                </Tooltip>
            )
        },
        {
            field: "is_active",
            headerName: "Status",
            width: 90,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Active" : "Inactive"}
                    size="small"
                    color={params.value ? "success" : "error"}
                    variant="outlined"
                />
            )
        },
        {
            field: "reward_credits",
            headerName: "Credits",
            width: 85,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    color="warning"
                />
            )
        },
        {
            field: "question_count",
            headerName: "Questions",
            width: 95,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value || 0}
                    size="small"
                    variant="outlined"
                />
            )
        },
        {
            field: "completion_count",
            headerName: "Completions",
            width: 110,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value || 0}
                    size="small"
                    color="info"
                    variant="outlined"
                />
            )
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 180,
            filterable: false,
            sortable: false,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Stack direction="row" spacing={0.5} justifyContent="center" width="100%">
                    <Tooltip title="Edit Survey">
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEditClick(params.row)}
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage Questions">
                        <IconButton
                            color="info"
                            size="small"
                            onClick={() => navigate(urls["adminSurveyQuestions"] + "?survey_uid=" + params.row.uid)}
                        >
                            <Assessment fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="View Responses">
                        <IconButton
                            color="success"
                            size="small"
                            onClick={() => navigate(urls["adminSurveyResponses"] + "?survey_uid=" + params.row.uid)}
                        >
                            <ListAlt fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Survey">
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(params.row)}
                        >
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        },
    ];

    if (pageDataQuery.isLoading) {
        return <PageLoading />;
    }

    if (pageDataQuery.isError) {
        return <PageDataErrorHandler error={pageDataQuery.error as ApiRequestFailed} />;
    }

    return (
        <Box sx={{ pb: 4 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
                mb={3}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold">
                        Surveys
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage all surveys and their configurations
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutline />}
                    onClick={handleCreateClick}
                    sx={{ minWidth: { xs: "100%", sm: "auto" } }}
                >
                    Create Survey
                </Button>
            </Stack>

            <Card>
                <CardContent sx={{ p: 0 }}>
                    <DataGrid
                        rows={pageData?.surveys || []}
                        columns={columns}
                        getRowId={(row) => row.uid}
                        autoHeight
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25 },
                            },
                        }}
                        disableRowSelectionOnClick
                        sx={{
                            "& .MuiDataGrid-cell": {
                                display: "flex",
                                alignItems: "center",
                                py: 1,
                            },
                            "& .MuiDataGrid-cell:focus": {
                                outline: "none",
                            },
                            "& .MuiDataGrid-row:hover": {
                                cursor: "pointer",
                            },
                        }}
                    />
                </CardContent>
            </Card>

            {/* Create Survey Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Create New Survey</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="Survey Title"
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            fullWidth
                            required
                            helperText="Minimum 5 characters"
                        />
                        <TextField
                            label="Description"
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                        />
                        <TextField
                            label="Display Order"
                            value={formDisplayOrder}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "" || /^\d+$/.test(val)) {
                                    setFormDisplayOrder(val);
                                }
                            }}
                            fullWidth
                            type="number"
                        />
                        {/* <TextField
                            label="Reward Credits"
                            value={formRewardCredits}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "" || /^\d+$/.test(val)) {
                                    setFormRewardCredits(val);
                                }
                            }}
                            fullWidth
                            type="number"
                            helperText="Credits awarded on survey completion (max 10,000)"
                        />
                        <Typography variant="subtitle2" color="text.secondary">
                            Additional Rewards Settings
                        </Typography> */}

                        {/* Checkbox 1: Reward Emails */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rewardEmailsEnabled}
                                        onChange={(e) => setRewardEmailsEnabled(e.target.checked)}
                                    />
                                }
                                label="Grant Reward Emails"
                            />
                            {rewardEmailsEnabled && (
                                <TextField
                                    label="Number of Reward Emails"
                                    value={formRewardCredits}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "" || /^\d+$/.test(val)) {
                                            setFormRewardCredits(val);
                                        }
                                    }}
                                    fullWidth
                                    type="number"
                                    size="small"
                                    sx={{ mt: 1 }}
                                    placeholder="e.g. 500"
                                />
                            )}
                        </Box>

                        {/* Checkbox 2: Reward Domains */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rewardDomainsEnabled}
                                        onChange={(e) => setRewardDomainsEnabled(e.target.checked)}
                                    />
                                }
                                label="Grant Reward Domains"
                            />
                            {rewardDomainsEnabled && (
                                <TextField
                                    label="Number of Reward Domains"
                                    value={rewardDomainsCount}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "" || /^\d+$/.test(val)) {
                                            setRewardDomainsCount(val);
                                        }
                                    }}
                                    fullWidth
                                    type="number"
                                    size="small"
                                    sx={{ mt: 1 }}
                                    placeholder="e.g. 2"
                                />
                            )}
                        </Box>

                        {/* Checkbox 3: Custom Reward */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rewardCustomEnabled}
                                        onChange={(e) => setRewardCustomEnabled(e.target.checked)}
                                    />
                                }
                                label="Grant Custom Reward"
                            />
                            {rewardCustomEnabled && (
                                <TextField
                                    label="Custom Reward Description / Details"
                                    value={rewardCustomText}
                                    onChange={(e) => setRewardCustomText(e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1 }}
                                    placeholder="e.g. 1 Month Pro Plan Upgrade"
                                />
                            )}
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formIsActive}
                                        onChange={(e) => setFormIsActive(e.target.checked)}
                                    />
                                }
                                label="Active"
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => createSurveyMutation.mutate()}
                        disabled={createSurveyMutation.isPending || formTitle.trim().length < 5}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Survey Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Edit Survey</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="Survey Title"
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            fullWidth
                            required
                            helperText="Minimum 5 characters"
                        />
                        <TextField
                            label="Description"
                            value={formDescription}
                            onChange={(e) => setFormDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                        />
                        <TextField
                            label="Display Order"
                            value={formDisplayOrder}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "" || /^\d+$/.test(val)) {
                                    setFormDisplayOrder(val);
                                }
                            }}
                            fullWidth
                            type="number"
                        />
                        {/* <TextField
                            label="Reward Credits"
                            value={formRewardCredits}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "" || /^\d+$/.test(val)) {
                                    setFormRewardCredits(val);
                                }
                            }}
                            fullWidth
                            type="number"
                            helperText="Credits awarded on survey completion (max 10,000)"
                        />
                        <Typography variant="subtitle2" color="text.secondary">
                            Additional Rewards Settings
                        </Typography> */}

                        {/* Checkbox 1: Reward Emails */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rewardEmailsEnabled}
                                        onChange={(e) => setRewardEmailsEnabled(e.target.checked)}
                                    />
                                }
                                label="Grant Reward Emails"
                            />
                            {rewardEmailsEnabled && (
                                <TextField
                                    label="Number of Reward Emails"
                                    value={formRewardCredits}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "" || /^\d+$/.test(val)) {
                                            setFormRewardCredits(val);
                                        }
                                    }}
                                    fullWidth
                                    type="number"
                                    size="small"
                                    sx={{ mt: 1 }}
                                    placeholder="e.g. 500"
                                />
                            )}
                        </Box>

                        {/* Checkbox 2: Reward Domains */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rewardDomainsEnabled}
                                        onChange={(e) => setRewardDomainsEnabled(e.target.checked)}
                                    />
                                }
                                label="Grant Reward Domains"
                            />
                            {rewardDomainsEnabled && (
                                <TextField
                                    label="Number of Reward Domains"
                                    value={rewardDomainsCount}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === "" || /^\d+$/.test(val)) {
                                            setRewardDomainsCount(val);
                                        }
                                    }}
                                    fullWidth
                                    type="number"
                                    size="small"
                                    sx={{ mt: 1 }}
                                    placeholder="e.g. 2"
                                />
                            )}
                        </Box>

                        {/* Checkbox 3: Custom Reward */}
                        <Box>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={rewardCustomEnabled}
                                        onChange={(e) => setRewardCustomEnabled(e.target.checked)}
                                    />
                                }
                                label="Grant Custom Reward"
                            />
                            {rewardCustomEnabled && (
                                <TextField
                                    label="Custom Reward Description / Details"
                                    value={rewardCustomText}
                                    onChange={(e) => setRewardCustomText(e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{ mt: 1 }}
                                    placeholder="e.g. 1 Month Pro Plan Upgrade"
                                />
                            )}
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formIsActive}
                                    onChange={(e) => setFormIsActive(e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => updateSurveyMutation.mutate()}
                        disabled={updateSurveyMutation.isPending || formTitle.trim().length < 5}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Survey</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the survey "{selectedSurvey?.title}"?
                        This will mark it as inactive.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => selectedSurvey && deleteSurveyMutation.mutate(selectedSurvey.uid)}
                        disabled={deleteSurveyMutation.isPending}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
